import type { QueryKey } from '@tanstack/react-query';
import type { ListingStatusEvent } from '@/services/echo.service';
import type { ListingPullStatus } from '@/types/listing';
import { listingProductQueryKeyPrefixes } from './listing.api';
import { consumeAuditInitiated, consumePullInitiated } from './listing.initiator';
import type { ListingEventAuthor } from './listing.initiator';

/**
 * Everything a `.listing.status.updated` broadcast decides: toast copy, the
 * inline failure sentence, query-invalidation keys, and the handler that
 * composes them (specs/product/listing-content-panel, AUT-231, WEB-002).
 * `ProtectedRoute.tsx` owns the subscription and nothing else;
 * `WEB-004`'s panel imports `listingFailureLine` to render the same
 * sentence inline rather than retyping it
 * (`data-flow.md § Flow: listing content pull`, hop 10).
 */

/**
 * Copy verbatim from
 * `design-input/listing-content-panel/prototype-extracted.md § Panel`.
 * `null` for every non-`succeeded` status, including `pulling` -- the toast
 * lives on the broadcast's terminal statuses only, and the poll fires none.
 */
export const listingToastCopy = (
  event: ListingStatusEvent
): { title: string; description: string } | null => {
  if (event.status !== 'succeeded') return null;

  return {
    title: event.is_first_pull ? 'Listing content pulled' : 'Listing content refreshed',
    description: `Pulled the latest live content for ${event.asin}.`,
  };
};

/**
 * The two failure sentences are declared here once. `null` for `succeeded`
 * and `pulling` -- there is nothing to say inline or in a toast for either.
 */
export const listingFailureLine = (status: ListingPullStatus): string | null => {
  switch (status) {
    case 'not_found':
      return 'This ASIN could not be found on Amazon.';
    case 'unavailable':
      return 'The listing could not be retrieved.';
    default:
      return null;
  }
};

/**
 * The only place in this task that builds an invalidation key. Returns the
 * two product-scoped prefixes for **every** status, `pulling` included: a
 * committed claim now precedes the audit's catalog read (`WEB-002`,
 * `decisions/a-listing-toast-is-addressed-by-user-and-tab-mark.md § The
 * rule handleListingBroadcast applies`, step 1), and this refetch is what
 * puts every open panel into the in-progress state and arms its 5 s poll
 * (`FR-3`). The broadcast payload carries no brand id, so these are
 * prefixes, not full keys (`listing.api.ts § listingProductQueryKeyPrefixes`).
 */
export const listingInvalidationKeys = (event: ListingStatusEvent): QueryKey[] => {
  return [...listingProductQueryKeyPrefixes(event.product_id)];
};

/**
 * Classifies who triggered the event, from the key's presence first: a
 * missing key is not a `null` (an old backend, not a foreign user) and
 * must not be read as `not_mine` -- that would silence a user's own
 * `Refresh` for the length of a deploy gap
 * (`decisions/a-listing-toast-is-addressed-by-user-and-tab-mark.md §
 * Consequences`).
 */
const classifyAuthor = (event: ListingStatusEvent, accountId: number): ListingEventAuthor => {
  if (!('triggered_by_user_id' in event)) return 'unknown';

  const { triggered_by_user_id: triggeredBy } = event;
  if (triggeredBy != null && triggeredBy === accountId) return 'mine';

  return 'not_mine';
};

export interface ListingBroadcastEffects {
  invalidate: (key: QueryKey) => void;
  toastSuccess: (title: string, description: string) => void;
  toastError: (message: string) => void;
}

/**
 * The whole of what a `.listing.status.updated` broadcast does, in one
 * place. `ProtectedRoute.tsx` owns the subscription and supplies the two
 * effects; everything that decides *whether* to do anything lives here,
 * where it can be tested without mounting the app shell.
 *
 * The two halves are deliberately scoped differently:
 *
 * - **Invalidation is org-wide.** The channel is `organization.{orgId}`
 *   and every tab that has this product cached should show the new
 *   content. `usePullListing.onSuccess` invalidates the same key for the
 *   initiator; a duplicate is harmless (TanStack dedupes concurrent
 *   refetches) and both are intended -- the mutation arms the poll for the
 *   initiator, the broadcast serves every other open tab.
 * - **The toast is filtered by author and by tab.** `triggered_by_user_id`
 *   says which account triggered the pull; a mark in `sessionStorage` says
 *   which tab asked. Both must agree before a toast fires, or a colleague
 *   pulling one ASIN -- or a colleague's audit -- would interrupt everyone
 *   else in the organization, on every screen, with a result they did not
 *   ask for (`listing.initiator.ts`,
 *   `decisions/a-listing-toast-is-addressed-by-user-and-tab-mark.md`).
 */
export const handleListingBroadcast = (
  event: ListingStatusEvent,
  accountId: number,
  { invalidate, toastSuccess, toastError }: ListingBroadcastEffects,
): void => {
  for (const key of listingInvalidationKeys(event)) {
    invalidate(key);
  }

  // Non-terminal first, and before either mark is touched: `pulling` is
  // what puts every open panel into its in-progress state, but nothing is
  // spent yet -- the terminal event still owes the toast.
  if (event.status === 'pulling') return;

  const author = classifyAuthor(event, accountId);

  // Both marks are evaluated on every terminal event, never
  // short-circuited: a tab that both started this pull itself and ran the
  // audit that produced it holds both marks and must spend both
  // (`decisions/…tab-mark.md § The same user's own fetch satisfies the
  // audit mark, by design`).
  const listingHit = consumePullInitiated(event.product_id, author);
  const auditHit = consumeAuditInitiated(event.asin, author);

  if (!listingHit && !auditHit) return;

  const copy = listingToastCopy(event);
  if (copy) {
    toastSuccess(copy.title, copy.description);
    return;
  }

  const failureLine = listingFailureLine(event.status);
  if (failureLine) {
    toastError(failureLine);
  }
};
